import {
  Box,
  Slide,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import VerificationLayout from "../../../layout/VerificationLayout.component";
import { ArtifactStyle } from "./artifactResponse/VerficationArtifactsStyles";
import { useVerification } from "../../../../context/Verification.context";
import TextResponse from "./artifactResponse/TextResponse";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import SurveyResponse from "./artifactResponse/SurveyResponse";
import RatingResponse from "./artifactResponse/RatingResponse";
import ReviewResponse from "./artifactResponse/ReviewResponse";
import AppointmentResponse from "./artifactResponse/AppointmentResponse";
import DealerAppResponse from "./artifactResponse/DealerAppResponse";
import VideoPlayer from "../../admin/dealership/tabs/VideoPlayer.component";
import VehicleTransactionResponse from "./artifactResponse/VehicleTransactionResponse";
import { normalizePlaybackUrl, isWalkthroughArtifact } from "../../../../utils/videoPlayer";

import {
  CSI_ALERT,
  FETCH_VEHICLE_TRANSACTION,
  MANAGE_ARTIFACTS_RESPONSE,
  // REMOVED: SUBMIT_REFERRAL — this component is the direct/in-app
  // verification carousel. It should only ever call MANAGE_ARTIFACTS_RESPONSE
  // for referrals (which correctly leaves is_followup at false). SUBMIT_REFERRAL
  // hits submitReferralLink.js on the backend, which is reserved exclusively
  // for the genuine follow-up link flow (SubmitReferrals.jsx) and always sets
  // is_followup = true. Calling it here was flipping is_followup to true on
  // every direct referral submission and creating a duplicate DB row.
} from "../../../../graphql/mutation/mutation";
import FetchAIVideoUrl from "../../heygenAI/FetchAIVideoUrl";
import PaginationComponent from "./artifactResponse/Pagination.component";
import ToggleComponent from "../../heygenAI/Toggle.component";
import WarrantyPresentation from "./warranty/WarrantyPresentation.component";
import AppButton from "../../../base/AppButton.component";

const PORTRAIT_MOBILE_QUERY = "(orientation: portrait) and (max-width: 900px)";

const useIsPortraitMobile = () => {
  const [isPortraitMobile, setIsPortraitMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(PORTRAIT_MOBILE_QUERY).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(PORTRAIT_MOBILE_QUERY);
    const handleChange = () => setIsPortraitMobile(mq.matches);
    handleChange();
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  return isPortraitMobile;
};

const RenderArtifactContent = ({
  artifactsArray,
  setClose,
  isPullComplete,
  isThankyou,
  fetchTrigger,
  artifactRefetch,
  artifactsLoading,
}) => {
  const verification = useVerification();
  const isPortraitMobile = useIsPortraitMobile();

  const safeParseJSON = (str) => {
    if (!str) return {};
    if (typeof str === "object") return str;

    try {
      return JSON.parse(str);
    } catch (e) {
      return {};
    }
  };
  const [fetchVehicle] = useMutation(
    FETCH_VEHICLE_TRANSACTION
  );

  // Must be computed before isArtifactVisible so the closure reference is valid
  const isWarrantyModuleEnabled =
    !Array.isArray(verification?.artifacts) &&
    Boolean(
      verification?.artifacts?.warrantyArtifact?.is_enabled &&
        verification?.artifacts?.has_warranty_coverages,
    );

  const isArtifactVisible = (artifact) => {
    if (!artifact || !Array.isArray(artifactsArray)) return false;

    // WalkMe synthetic first slide — always visible when present
    if (isWalkthroughArtifact(artifact)) return true;

    if (artifact.__typename === "ReviewUrl") {
      return verification?.artifactsResponse?.reviewResponse?.answer === "yes";
    }

    const questionArtifact = artifactsArray.find(
      (item) =>
        item.artifact_type_id === 17 ||
        item.vehicle_information ||
        item.es_vehicle_information,
    );

    if (questionArtifact && artifact.position <= questionArtifact.position) {
      return true;
    }

    const rule = verification.artifacts?.displayRules?.find(
      (r) =>
        r.artifact_id === artifact.id &&
        r.artifact_type_id === artifact.artifact_type_id,
    );

    // Warranty artifact — only show when the warranty module is enabled AND an
    // engine type is selected. This is a gate that can only exclude Warranty
    // early; it does NOT return true early, so the vehicle_rules /
    // transaction_rules bubble check below still applies to Warranty just
    // like every other artifact type.
    if (artifact.__typename === "WarrantyArtifact") {
      const hasEngine = Boolean(
        verification?.artifactsResponse?.vehicleTransactionResponse?.engine,
      );
      if (!isWarrantyModuleEnabled || !hasEngine) return false;
    }

    if (!rule) return true;

    const vehicleRules = safeParseJSON(rule.vehicle_rules);
    const transactionRules = safeParseJSON(rule.transaction_rules);

    const selectedVehicle = verification.artifactsResponse?.vehicleType;

    const selectedTransaction = verification.artifactsResponse?.transactionType;

    if (!selectedVehicle && !selectedTransaction) return true;

    if (selectedVehicle && vehicleRules[selectedVehicle] === false) {
      return false;
    }

    if (
      selectedTransaction &&
      transactionRules[selectedTransaction] === false
    ) {
      return false;
    }

    return true;
  };

  const navigate = useNavigate();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [reviewScreen, setReviewScreen] = useState(false);
  const [videoScreen, setVideoScreen] = useState(false);
  const [appointmentScreen, setAppointmentScreen] = useState(false);
  const [referralScreen, setRefferalScreen] = useState(false);
  const [dealerAppScreen, setDealerAppScreen] = useState(false);
  const [surveyScreen, setSurveyScreen] = useState(false);
  const [duration, setDuration] = useState(0);
  const [animateIn, setAnimateIn] = useState(false);
  const [showSurveyButton, setShowSurveyButton] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [vehicleTransactionScreen, setVehicleTransactionScreen] =
    useState(false);
  const [loading, setIsLoading] = useState(true);
  const [showWarrantyPresentation, setShowWarrantyPresentation] = useState(false);
  const [warrantyDismissed, setWarrantyDismissed] = useState(false);
  const [lastArtifactVideoCompleted, setLastArtifactVideoCompleted] =
    useState(false);
  const [hasCurrentStepInteraction, setHasCurrentStepInteraction] =
    useState(false);

  const markCurrentStepInteraction = () => {
    setHasCurrentStepInteraction(true);
  };

  const [manageArtifactResponse] = useMutation(MANAGE_ARTIFACTS_RESPONSE);

  // REMOVED: const [submitReferral] = useMutation(SUBMIT_REFERRAL);
  // No longer needed here — see note on the SUBMIT_REFERRAL import above.

  const [csiAlert] = useMutation(CSI_ALERT);

  const visibleArtifacts = Array.isArray(artifactsArray)
    ? artifactsArray.filter((a) => isArtifactVisible(a))
    : [];

  // KW/IW walkthrough slides play in the carousel but are not part of "N of M"
  // (same as Insurance WT: Next only). Keeps pull + thank-you counts aligned.
  const isCurrentWalkthrough = isWalkthroughArtifact(
    artifactsArray?.[currentIndex],
  );
  const paginatedArtifacts = visibleArtifacts.filter(
    (a) => !isWalkthroughArtifact(a),
  );

  const visibleCurrentIndex =
    !isCurrentWalkthrough && artifactsArray?.[currentIndex]
      ? Math.max(
          0,
          paginatedArtifacts.findIndex(
            (a) => a === artifactsArray[currentIndex],
          ),
        )
      : 0;

  const visibleTotalPages = paginatedArtifacts.length;

  const isAvatarMode = Boolean(
    verification?.aiData?.isAI && verification?.aiData?.spokeperson,
  );

  const currentArtifact = artifactsArray?.[currentIndex];

  // The current artifact is the warranty artifact
  const isCurrentWarrantyArtifact =
    currentArtifact?.__typename === "WarrantyArtifact" ||
    Boolean(currentArtifact?.is_enabled);

  // Is the warranty artifact (or whatever artifact is current) the LAST
  // artifact in the visible, paginated sequence? Used so Stop/Next from the
  // warranty presentation lands on Thank You instead of a blank screen when
  // there's nothing left to advance to.
  const isLastVisibleArtifact =
    visibleTotalPages > 0 && visibleCurrentIndex === visibleTotalPages - 1;

  const isReviewRatingArtifact = (artifact) =>
    artifact?.__typename === "ReviewUrl" ||
    artifact?.artifact_type_id === 15 ||
    Boolean(artifact?.facebook || artifact?.google || artifact?.dealer_rater);

  // Only count video sources for the active presentation mode.
  // Walkthrough/video share the same uploaded file via url and ai_url.
  const lastArtifactHasVideo = isAvatarMode
    ? Boolean(
        currentArtifact?.ai_url?.english_url ||
          currentArtifact?.ai_url?.spanish_url,
      )
    : Boolean(currentArtifact?.url || currentArtifact?.es_url);

  const lastArtifactHasEndOptions = Boolean(
    currentArtifact?.question ||
      currentArtifact?.es_question ||
      currentArtifact?.__typename === "SurveyArtifact" ||
      currentArtifact?.artifact_type_id === 2 ||
      currentArtifact?.__typename === "VehicleTransactionArtifact" ||
      currentArtifact?.preference_options?.length > 0 ||
      currentArtifact?.es_preference_options?.length > 0 ||
      currentArtifact?.__typename === "AppointmentArtifact" ||
      currentArtifact?.artifact_type_id === 6 ||
      currentArtifact?.__typename === "DealerAppUrl" ||
      currentArtifact?.review_question ||
      currentArtifact?.es_review_question ||
      currentArtifact?.artifact_type_id === 7 ||
      isReviewRatingArtifact(currentArtifact),
  );

  const artifactsResponse = verification?.artifactsResponse;
  const lastArtifactOptionSelected = (() => {
    const artifact = currentArtifact;
    if (!artifact || !artifactsResponse || !hasCurrentStepInteraction) {
      return false;
    }

    // Rating step — check before review_question/type 7 (ReviewUrl also has type 7).
    if (isReviewRatingArtifact(artifact)) {
      return Boolean(artifactsResponse.reviewResponse?.isReview);
    }

    if (artifact?.review_question || artifact?.es_review_question) {
      return Boolean(artifactsResponse.reviewResponse?.answer);
    }

    if (
      artifact?.question ||
      artifact?.es_question ||
      artifact?.__typename === "SurveyArtifact" ||
      artifact?.artifact_type_id === 2
    ) {
      return Boolean(
        artifactsResponse.surveyResponse?.some(
          (r) => r?.question_id === artifact?.id,
        ),
      );
    }

    if (artifact?.__typename === "VehicleTransactionArtifact") {
      return Boolean(
        artifactsResponse.vehicleType ||
          artifactsResponse.transactionType ||
          artifactsResponse.vehicleTransactionResponse?.vehicle_type ||
          artifactsResponse.vehicleTransactionResponse?.transaction_type,
      );
    }

    if (
      artifact?.preference_options?.length > 0 ||
      artifact?.es_preference_options?.length > 0 ||
      artifact?.__typename === "AppointmentArtifact" ||
      artifact?.artifact_type_id === 6
    ) {
      return Boolean(
        artifactsResponse.appointmentOption?.preference ||
          artifactsResponse.appointmentResponse?.preference ||
          artifactsResponse.appointmentResponse?.date,
      );
    }

    if (artifact?.__typename === "DealerAppUrl") {
      return Boolean(artifactsResponse.dealerAppResponse?.dealer_review);
    }

    return false;
  })();

  useEffect(() => {
    setHasCurrentStepInteraction(false);
  }, [currentIndex, currentArtifact?.id]);

  const handleLastArtifactVideoEnded = () => {
    const isLastVisible =
      visibleTotalPages > 0 && visibleCurrentIndex === visibleTotalPages - 1;
    if (isLastVisible) {
      setLastArtifactVideoCompleted(true);
    }
  };

  // Warranty popup: opens as soon as the carousel reaches the warranty
  // artifact's step (its saved position in the Execution Sequence).
  useEffect(() => {
    if (!isCurrentWarrantyArtifact) return;
    if (isWarrantyModuleEnabled) {
      setShowWarrantyPresentation(true);
    }
  }, [isCurrentWarrantyArtifact, isWarrantyModuleEnabled]);

  // Reset warranty popup when the user navigates away from the warranty artifact
  useEffect(() => {
    if (!isCurrentWarrantyArtifact) {
      setShowWarrantyPresentation(false);
      setWarrantyDismissed(false);
    }
  }, [isCurrentWarrantyArtifact]);

  useEffect(() => {
    if (
      (verification.artifactsResponse?.reviewResponse?.answer === "no" ||
        !verification?.artifactsResponse?.reviewResponse) &&
      artifactsArray[verification.artifactIndex]?.review_question
    ) {
      setCurrentIndex(verification.artifactIndex);
    } else if (
      isThankyou &&
      verification?.customer?.premium &&
      verification.artifactIndex < artifactsArray?.length - 1
    ) {
      setCurrentIndex(verification.artifactIndex + 1);
    }
  }, [isThankyou]);

  useEffect(() => {
    const artifact = artifactsArray[currentIndex];

    if (!artifact) return;

    // WalkMe slide has no survey / appointment / etc. overlays
    if (isWalkthroughArtifact(artifact)) {
      setVehicleTransactionScreen(false);
      setSurveyScreen(false);
      setAppointmentScreen(false);
      setDealerAppScreen(false);
      setReviewScreen(false);
      setVideoScreen(false);
      return;
    }

    setVehicleTransactionScreen(
      artifact.__typename === "VehicleTransactionArtifact",
    );

    setSurveyScreen(!!(artifact.question || artifact.es_question));

    setAppointmentScreen(
      !!(
        artifact.preference_options?.length > 0 ||
        artifact.es_preference_options?.length > 0
      ),
    );

    setDealerAppScreen(artifact.__typename === "DealerAppUrl");

    setReviewScreen(!!artifact.review_question);
  }, [currentIndex, artifactsArray]);

  useEffect(() => {
    if (verification?.customer?.userId && currentIndex === 0) {
      manageArtifactResponse({
        variables: {
          responseInput: {
            dealership_id: parseInt(verification?.dealershipId),
            employee_user_id: parseInt(verification?.employeeId),
            user_id: parseInt(verification?.customer?.userId),
            watched_artifacts: true,
          },
        },
      });
    }
  }, [currentIndex === 0]);

  // NEW: mark the customer as having reached the LAST VISIBLE artifact in
  // the carousel — i.e. actually completed the Knowledge Hub experience,
  // not just navigated through the raw (possibly-filtered) array. Uses
  // visibleCurrentIndex/visibleTotalPages (the same values driving
  // pagination and isLastVisibleArtifact) so it can't get stuck if the last
  // raw artifact happens to be filtered out. Guards against the synthetic
  // walkthrough slide, and only fires once per session via the ref.
  const hasMarkedCompleteRef = useRef(false);

  useEffect(() => {
    if (
      verification?.customer?.userId &&
      visibleTotalPages > 0 &&
      visibleCurrentIndex === visibleTotalPages - 1 &&
      !isCurrentWalkthrough &&
      !hasMarkedCompleteRef.current
    ) {
      hasMarkedCompleteRef.current = true;
      manageArtifactResponse({
        variables: {
          responseInput: {
            dealership_id: parseInt(verification?.dealershipId),
            employee_user_id: parseInt(verification?.employeeId),
            user_id: parseInt(verification?.customer?.userId),
            completed_artifacts: true,
          },
        },
      });
    }
  }, [visibleCurrentIndex, visibleTotalPages, isCurrentWalkthrough]);

  const prevIndexRef = useRef(currentIndex);

  useEffect(() => {
    const artifact = artifactsArray?.[currentIndex];

    const didNavigate = prevIndexRef.current !== currentIndex;

    prevIndexRef.current = currentIndex;

    if (!artifact?.id || !didNavigate) return;

    const isSurvey = artifact?.question || artifact?.es_question;

    if (!isSurvey) return;

    const hasOther = verification?.artifactsResponse?.surveyResponse?.some(
      (r) => r?.question_id === artifact?.id && r?.isOther,
    );

    if (!hasOther) return;

    verification?.setArtifactsResponse?.((prev) => {
      const surveyResponse = prev?.surveyResponse ?? [];

      const filtered = surveyResponse.filter(
        (r) => !(r?.question_id === artifact?.id && r?.isOther),
      );

      if (filtered.length === surveyResponse.length) {
        return prev;
      }

      return {
        ...prev,
        surveyResponse: filtered,
      };
    });

    verification?.setAIData?.((prev) => ({
      ...prev,
      isOther: false,
    }));
  }, [
    currentIndex,
    artifactsArray,
    verification?.artifactsResponse?.surveyResponse,
  ]);

  const FectchTrigger = async () => {
    fetchTrigger({
      variables: {
        requestInput: {
          employee_user_id: parseInt(verification?.employeeId),
          user_id: verification?.customer?.userId,
          dealership_id: parseInt(verification.dealershipId),
          d_startDate: verification?.fetchData?.d_startDate,
          d_endDate: verification?.fetchData?.d_endDate,
          f_startDate: verification?.fetchData?.f_startDate,
          f_endDate: verification?.fetchData?.f_endDate,
          pageCount: verification?.fetchData?.pageCount,
          rowsPerPage: verification?.fetchData?.rowsPerPage,
          search_employee: verification?.fetchData?.searchEmployee,
          search_customer: verification?.fetchData?.searchCustomer,
          isEmployeeOnly: false,
          timezone: new Date().getTimezoneOffset(),
        },
      },
    });
  };

  useEffect(() => {
    if (currentIndex === artifactsArray.length - 1) {
      if (
        reviewScreen ||
        referralScreen ||
        videoScreen ||
        appointmentScreen ||
        dealerAppScreen ||
        surveyScreen
      ) {
        saveArtifactResponses();
      }

      if (isThankyou) {
        setSurveyScreen(false);
        FectchTrigger();
      }
    }

    if (
      verification?.artifactsResponse?.reviewResponse?.answer === "no" &&
      currentIndex === artifactsArray.length - 2 &&
      (artifactsArray[currentIndex]?.facebook ||
        artifactsArray[currentIndex]?.dealer_rater ||
        artifactsArray[currentIndex]?.google)
    ) {
      saveArtifactResponses();

      if (isThankyou) {
        FectchTrigger();
      }
    }
  }, [
    currentIndex,
    reviewScreen,
    referralScreen,
    videoScreen,
    appointmentScreen,
    dealerAppScreen,
    surveyScreen,
  ]);

  const saveArtifactResponses = () => {
    const { artifactsResponse, dealershipId, customer, employeeId } =
      verification;

    const handleResponse = (condition, responseKey, callback) => {
      if (!condition) return;

      const responseData = artifactsResponse?.[responseKey];

      if (!responseData) return;

      let payload;

      if (responseKey === "videoResponse") {
        const filterIsCurrent =
          verification?.artifactsResponse?.videoResponse?.find(
            (x) => x.isCurrent,
          );

        if (filterIsCurrent) {
          const newVideoResponse = {
            id: filterIsCurrent?.id,
            user_id: filterIsCurrent?.user_id,
            video_id: filterIsCurrent?.video_id,
            total_secs: filterIsCurrent?.total_secs,
            watched_secs: filterIsCurrent?.watched_secs,
            last_watched_secs: filterIsCurrent?.last_watched_secs,
            created_by: filterIsCurrent?.created_by,
            updated_by: filterIsCurrent?.updated_by,
          };

          manageArtifactResponse({
            variables: {
              responseInput: {
                dealership_id: parseInt(verification.dealershipId),
                employee_user_id: parseInt(verification?.employeeId),
                videoResponse: newVideoResponse,
              },
            },
          }).then((data) => {
            if (data?.data?.manageArtifactResponse?.id) {
              const updatedResponse = {
                ...newVideoResponse,
                id: data.data.manageArtifactResponse.id,
                isCurrent: false,
              };

              verification.setArtifactsResponse((prev) => {
                const existingResponses = prev?.videoResponse || [];

                const alreadyExists = existingResponses.some(
                  (r) => r.video_id === updatedResponse.video_id,
                );

                return {
                  ...prev,
                  videoResponse: alreadyExists
                    ? existingResponses.map((r) =>
                        r.video_id === updatedResponse.video_id
                          ? updatedResponse
                          : r,
                      )
                    : [...existingResponses, updatedResponse],
                };
              });
            }
          });
        }
      } else if (responseKey === "surveyResponse") {
        const filterIsCurrent = (
          verification?.artifactsResponse?.surveyResponse || []
        ).find((r) => r.isCurrent);

        if (filterIsCurrent) {
          const newSurveyResponse = {
            id: filterIsCurrent?.id,
            user_id: filterIsCurrent?.user_id,
            question_id: filterIsCurrent?.question_id,
            answer: filterIsCurrent?.answer,
            created_by: filterIsCurrent?.created_by,
            updated_by: filterIsCurrent?.updated_by,
          };

          manageArtifactResponse({
            variables: {
              responseInput: {
                dealership_id: parseInt(verification.dealershipId),
                employee_user_id: parseInt(verification?.employeeId),
                surveyResponse: newSurveyResponse,
              },
            },
          }).then((data) => {
            if (data?.data?.manageArtifactResponse?.id) {
              const updatedResponse = {
                ...newSurveyResponse,
                id: data.data.manageArtifactResponse.id,
                isCurrent: false,
                isOther: filterIsCurrent?.isOther,
              };

              verification.setArtifactsResponse((prev) => {
                const existingResponses = prev?.surveyResponse || [];

                const alreadyExists = existingResponses.some(
                  (r) => r.question_id === updatedResponse.question_id,
                );

                return {
                  ...prev,
                  surveyResponse: alreadyExists
                    ? existingResponses.map((r) =>
                        r.question_id === updatedResponse.question_id
                          ? updatedResponse
                          : r,
                      )
                    : [...existingResponses, updatedResponse],
                };
              });
            }
          });
        }
      } else {
        if (Array.isArray(responseData)) {
          payload = responseData.map((item) => {
            const existing = artifactsResponse?.[responseKey]?.find(
              (r) => r.question_id === item.question_id || r.name === item.name,
            );

            return {
              ...item,
              id: existing?.id,
            };
          });
        } else {
          payload = {
            ...responseData,
            id: artifactsResponse?.[responseKey]?.id,
          };
        }

        manageArtifactResponse({
          variables: {
            responseInput: {
              employee_user_id: parseInt(employeeId),
              dealership_id: parseInt(dealershipId),
              [responseKey]: payload,
            },
          },
        }).then((data) => {
          // REMOVED: the submitReferral(...) call that used to fire here
          // for responseKey === "referralResponse". manageArtifactResponse
          // (called above) already saves the referral correctly via the
          // backend's referralResponse block, and now also sends the
          // manager notification email/SMS itself — without ever touching
          // is_followup, which must stay false for this direct/in-app path.
          // submitReferralLink.js (and its is_followup = true behavior) is
          // reserved exclusively for the real follow-up link flow in
          // SubmitReferrals.jsx.

          if (data?.data?.manageArtifactResponse?.id) {
            verification.setArtifactsResponse((prev) => ({
              ...prev,
              [responseKey]: {
                ...prev?.[responseKey],
                id: data.data.manageArtifactResponse.id,
              },
            }));
          }
        });
      }

      callback(false);
    };

    if (reviewScreen && artifactsResponse?.reviewResponse?.review_question_id) {
      if (artifactsResponse?.reviewResponse?.answer === "no") {
        csiAlert({
          variables: {
            alertInput: {
              user_id: parseInt(customer?.userId),
            },
          },
        });
      }

      handleResponse(true, "reviewResponse", setReviewScreen);
    }

    handleResponse(
      referralScreen && artifactsResponse?.referralResponse?.[0]?.name,
      "referralResponse",
      setRefferalScreen,
    );

    handleResponse(videoScreen, "videoResponse", setVideoScreen);

    handleResponse(surveyScreen, "surveyResponse", setSurveyScreen);

    handleResponse(
      appointmentScreen,
      "appointmentResponse",
      setAppointmentScreen,
    );

    handleResponse(dealerAppScreen, "dealerAppResponse", setDealerAppScreen);

    // vehicle transaction response
    if (vehicleTransactionScreen) {
      const vehicleTransactionResponse =
        verification?.artifactsResponse?.vehicleTransactionResponse;

      if (vehicleTransactionResponse) {
        fetchVehicle({
          variables: {
            fetchVehicleTransactionInput: {
              vehicleTransactionResponse: {
                id: vehicleTransactionResponse?.id || 0,
                vehicle_id: vehicleTransactionResponse?.vehicle_id || 0,
                artifact_id: artifactsArray[currentIndex]?.id,
                dealership_id: parseInt(verification?.dealershipId),
                employee_user_id: parseInt(verification?.employeeId),
                user_id: parseInt(verification?.customer?.userId),

                vehicle_type: vehicleTransactionResponse?.vehicle_type,
                transaction_type: vehicleTransactionResponse?.transaction_type,

                year: vehicleTransactionResponse?.year,
                make: vehicleTransactionResponse?.make,
                model: vehicleTransactionResponse?.model,

                created_by: parseInt(verification?.employeeId),
                updated_by: parseInt(verification?.employeeId),
              },
            },
          },
        }).then((data) => {
          if (data?.data?.fetchVehicle?.vehicleTransactionResponse) {
            const savedIds = data.data.fetchVehicle.vehicleTransactionResponse;
            verification.setArtifactsResponse((prev) => ({
              ...prev,
              vehicleTransactionResponse: {
                ...prev.vehicleTransactionResponse,
                id: savedIds.id,
                vehicle_id: savedIds.vehicle_id,
              },
            }));
          }
        });
      }

      setVehicleTransactionScreen(false);
    }

  };

  const workflowSkip = () => {
    verification.setArtifactIndex(currentIndex);

    if (parseInt(verification?.workflow) === 3) {
      navigate(`/verification/requestQuote`, {
        replace: true,
      });
    } else if (parseInt(verification?.workflow) === 2) {
      navigate(`/verification/thankYou`, {
        replace: true,
      });
    } else {
      navigate(`/verification/tradeVehicle`, {
        replace: true,
      });
    }
  };

  const heygenload = async () => {
    setAnimateIn(false);
    setDuration(0);
    setShowSurveyButton(false);
  };

  const clearInstructionVideoCompletedForArtifact = (index) => {
    const leavingArtifact = artifactsArray?.[index];

    const leavingArtifactId = leavingArtifact?.id;

    if (leavingArtifactId == null) return;

    verification?.setAIData?.((prev) => {
      const byArtifact = prev?.instructionVideoCompletedByArtifact ?? {};

      const { [leavingArtifactId]: _, ...rest } = byArtifact;

      return {
        ...prev,
        instructionVideoCompletedByArtifact: rest,
      };
    });

    if (leavingArtifact?.question || leavingArtifact?.es_question) {
      verification?.setArtifactsResponse?.((prev) => {
        const surveyResponse = prev?.surveyResponse ?? [];

        const filtered = surveyResponse.filter(
          (r) => !(r?.question_id === leavingArtifactId && r?.isOther),
        );

        if (filtered.length === surveyResponse.length) {
          return prev;
        }

        return {
          ...prev,
          surveyResponse: filtered,
        };
      });

      verification?.setAIData?.((prev) => ({
        ...prev,
        isOther: false,
      }));
    }
  };

  const markWalkthroughSeenIfNeeded = () => {
    const current = artifactsArray?.[currentIndex];
    if (!isWalkthroughArtifact(current)) return;
    if (current?.walkthroughType === "insurance") {
      verification.setHasSeenInsuranceWalkthrough(true);
    } else {
      verification.setHasSeenWalkthroughVideo(true);
    }
  };

  const handleNext = async () => {
    clearInstructionVideoCompletedForArtifact(currentIndex);

    await heygenload();

    markWalkthroughSeenIfNeeded();

    setCurrentIndex((prevIndex) => {
      let nextIndex = prevIndex + 1;

      while (nextIndex < artifactsArray?.length) {
        if (!isArtifactVisible(artifactsArray[nextIndex])) {
          nextIndex += 1;
        } else {
          break;
        }
      }

      if (nextIndex >= artifactsArray.length) {
        return prevIndex;
      }

      return nextIndex;
    });

    setDirection("left");

    saveArtifactResponses();

    if (isPullComplete) {
      workflowSkip();
    }
  };

  const handlePrevious = async () => {
    clearInstructionVideoCompletedForArtifact(currentIndex);

    await heygenload();

    setCurrentIndex((prevIndex) => {
      let previousIndex = prevIndex - 1;

      while (previousIndex >= 0) {
        if (!isArtifactVisible(artifactsArray[previousIndex])) {
          previousIndex -= 1;
        } else {
          break;
        }
      }

      if (previousIndex < 0) {
        return prevIndex;
      }

      return previousIndex;
    });

    setDirection("right");

    saveArtifactResponses();

    if (isPullComplete) {
      workflowSkip();
    }
  };

  const handleClose = async () => {
    await heygenload();

    markWalkthroughSeenIfNeeded();

    if (isThankyou) {
      await FectchTrigger();
    }

    setClose(true);

    verification.setArtifactIndex(currentIndex);

    verification.setIsArtifactOpen(true);

    saveArtifactResponses();

    if (isPullComplete) {
      workflowSkip();
    }
  };

  const selectedUrl = normalizePlaybackUrl(
    !verification.aiData.spanish
      ? artifactsArray[currentIndex]?.ai_url?.english_url ||
          artifactsArray[currentIndex]?.url
      : artifactsArray[currentIndex]?.ai_url?.spanish_url ||
          artifactsArray[currentIndex]?.es_url,
  );

  return (
    <VerificationLayout><Box
        sx={{
          opacity: verification?.isResending ? 0.7 : 1,
          pointerEvents: verification?.isResending ? "none" : "auto",
          transition: "opacity 0.2s ease-in-out",
          height: "100%",
          width: "100%",
        }}
      >
        {verification?.aiData?.isAI &&
          verification?.aiData?.spokeperson ? (
          <FetchAIVideoUrl
            artifact={artifactsArray[currentIndex]}
            manageArtifactResponse={manageArtifactResponse}
            setSurveyScreen={setSurveyScreen}
            setAppointmentScreen={setAppointmentScreen}
            handleClose={handleClose}
            videoUrl={artifactsArray[currentIndex]?.ai_url}
            currentIndex={visibleCurrentIndex}
            totalPages={visibleTotalPages}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            isAI={verification?.aiData?.isAI}
            animateIn={animateIn}
            setAnimateIn={setAnimateIn}
            showSurveyButton={showSurveyButton}
            setShowSurveyButton={setShowSurveyButton}
            setDealerAppScreen={setDealerAppScreen}
            duration={duration}
            setDuration={setDuration}
            direction={direction}
            isOtherSelected={isOtherSelected}
            setVideoScreen={setVideoScreen}
            setReviewScreen={setReviewScreen}
            setRefferalScreen={setRefferalScreen}
            reviewScreen={reviewScreen}
            referralScreen={referralScreen}
            setVehicleTransactionScreen={setVehicleTransactionScreen}
            onMainVideoEnded={() => {
                handleLastArtifactVideoEnded();
              }}
            onStepInteraction={markCurrentStepInteraction}
            walkthrough={isWalkthroughArtifact(artifactsArray[currentIndex])}
          />
        ) : (
          <Box sx={ArtifactStyle.mainBox}>
<Box sx={ArtifactStyle.toggleZone}>
  <ToggleComponent handleClose={handleClose} />
</Box>

            <Box sx={ArtifactStyle.contentBox}>
              <Slide
                in={true}
                key={currentIndex}
                direction={direction}
                mountOnEnter
                unmountOnExit
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    overflowY: "scroll",
                  }}
                >
                  {isCurrentWarrantyArtifact ? (
                    <WarrantyPresentation
                      isPortraitMobile={isPortraitMobile}
                      onDismiss={() => {
                        if (isLastVisibleArtifact) {
                          verification.setArtifactIndex(currentIndex);
                          navigate(`/verification/thankYou`, { replace: true });
                        } else {
                          handleNext();
                        }
                      }}
                    />
                  ) : (
                    <>
                      {(isWalkthroughArtifact(artifactsArray[currentIndex]) ||
                        artifactsArray[currentIndex]?.text ||
                        artifactsArray[currentIndex]?.es_text) && (
                        <TextResponse artifact={artifactsArray[currentIndex]} />
                      )}
                      {(artifactsArray[currentIndex]?.url ||
                        artifactsArray[currentIndex]?.es_url) && (
                        <VideoPlayer
                          artifact={artifactsArray[currentIndex]}
                          setVideoScreen={setVideoScreen}
                          selectedUrl={
                            !verification.aiData.spanish
                              ? artifactsArray[currentIndex]?.url
                              : artifactsArray[currentIndex]?.es_url
                          }
                          setIsLoading={setIsLoading}
                          setAnimateIn={setAnimateIn}
                          onVideoEnded={() => {
                            handleLastArtifactVideoEnded();
                          }}
                        />
                      )}
                      {artifactsArray[currentIndex]?.__typename ===
                        "DealerAppUrl" && (
                        <DealerAppResponse
                          artifact={artifactsArray[currentIndex]}
                          setDealerAppScreen={setDealerAppScreen}
                        />
                      )}
                      {(artifactsArray[currentIndex]?.preference_options?.length >
                        0 ||
                        artifactsArray[currentIndex]?.es_preference_options
                          ?.length > 0) && (
                        <AppointmentResponse
                          artifact={artifactsArray[currentIndex]}
                          setAppointmentScreen={setAppointmentScreen}
                        />
                      )}
                      {artifactsArray[currentIndex]?.review_question && (
                        <ReviewResponse
                          artifact={artifactsArray[currentIndex]}
                          setReviewScreen={setReviewScreen}
                          setRefferalScreen={setRefferalScreen}
                          onStepInteraction={markCurrentStepInteraction}
                        />
                      )}
                      {artifactsArray[currentIndex]?.__typename === "ReviewUrl" &&
                        verification?.artifactsResponse?.reviewResponse?.answer ===
                          "yes" && (
                          <RatingResponse
                            artifact={artifactsArray[currentIndex]}
                            setReviewScreen={setReviewScreen}
                            onInteraction={markCurrentStepInteraction}
                          />
                        )}
                      {(artifactsArray[currentIndex]?.question ||
                        artifactsArray[currentIndex]?.es_question) && (
                        <SurveyResponse
                          artifact={artifactsArray[currentIndex]}
                          manageArtifactResponse={manageArtifactResponse}
                          setSurveyScreen={setSurveyScreen}
                          isOtherSelected={isOtherSelected}
                          onStepInteraction={markCurrentStepInteraction}
                        />
                      )}
                      {artifactsArray[currentIndex]?.__typename === "VehicleTransactionArtifact" && (
                        <VehicleTransactionResponse
                          artifact={artifactsArray[currentIndex]}
                          handleNext={handleNext}
                          handleClose={handleClose}
                          setVehicleTransactionScreen={setVehicleTransactionScreen}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Slide>
            </Box>

<Box sx={ArtifactStyle.paginationZone}>
  {isCurrentWalkthrough ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        pt: 1,
      }}
    >
      <AppButton
        buttonText="NEXT"
        size="medium"
        variant="contained"
        sx={{ width: { xs: "70%", sm: "50%" } }}
        clickHandler={handleNext}
      />
    </Box>
  ) : (
    <PaginationComponent
      currentIndex={visibleCurrentIndex}
      totalPages={visibleTotalPages}
      handleNext={handleNext}
      handlePrevious={handlePrevious}
    />
  )}
</Box>
          </Box>
        )}
      </Box></VerificationLayout>
  );
};

export default RenderArtifactContent;